/**
 * Strukturerad data (schema.org JSON-LD) som delas mellan sidor.
 *
 * Allt renderas i serverns HTML – antingen via ruttens head() (scripts) eller
 * inline med <JsonLd> – aldrig via Helmet, som bara körs i webbläsaren.
 */

import { ELPRIS_API_BASE } from "@/config/publicApis";
import { OPENAPI_URL } from "@/lib/openApi";
import { agreementsPhrase, type StatsInput } from "@/lib/siteStats";

export const SITE_URL = "https://elmarknad.se";
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

export type JsonLd = Record<string, unknown>;

/** JSON för <script type="application/ld+json">, säker att bädda in i HTML. */
export function serializeJsonLd(data: JsonLd): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}

/** head()-script för JSON-LD. */
export function jsonLdHeadScript(data: JsonLd) {
  return { type: "application/ld+json", children: serializeJsonLd(data) };
}

export interface FaqEntry {
  question: string;
  answer: string;
}

/** FAQPage med exakt de frågor och svar som visas på sidan. Null utan frågor. */
export function faqPageJsonLd(items: FaqEntry[]): JsonLd | null {
  const valid = items.filter((i) => i.question.trim() && i.answer.trim());
  if (valid.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: valid.map((i) => ({
      "@type": "Question",
      name: i.question.trim(),
      acceptedAnswer: { "@type": "Answer", text: i.answer.trim() },
    })),
  };
}

/**
 * Organization + WebSite för alla sidor. Antalet elavtal och elbolag kommer
 * från getHomeStats() (samma formulering som startsidan och /llms.txt); utan
 * statistik nämns inga siffror.
 */
export function siteGraphJsonLd(stats?: StatsInput | null): JsonLd {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": ORGANIZATION_ID,
        name: "Elmarknad.se",
        url: `${SITE_URL}/`,
        logo: `${SITE_URL}/lovable-uploads/elmarknad-logo-2025.png`,
        email: "info@elmarknad.se",
        areaServed: { "@type": "Country", name: "Sverige" },
        description: `Kostnadsfri jämförelsetjänst inom el och energi: ${agreementsPhrase(stats)}, spotpriser för SE1–SE4 och offerter på solceller, batterier, värmepumpar och elbilsladdare.`,
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        name: "Elmarknad.se",
        url: `${SITE_URL}/`,
        inLanguage: "sv-SE",
        publisher: { "@id": ORGANIZATION_ID },
      },
    ],
  };
}

const AREAS = ["SE1", "SE2", "SE3", "SE4"] as const;

/** Första dagen med kvarts-/timpriser i spot_prices (det elpris-API:t serverar). */
export const SPOT_PRICES_FIRST_DATE = "2021-12-27";

/**
 * Dataset för spotpriserna (/elpriser och /spotpris), med det öppna
 * elpris-API:t som distribution. Uppfyller Googles krav (name, description)
 * och de rekommenderade fälten.
 */
export function spotPriceDatasetJsonLd(pagePath: string): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "@id": `${SITE_URL}/elpriser#dataset`,
    name: "Spotpriser på el i Sverige (SE1–SE4)",
    description:
      "Spotpriser på el per kvart och timme för Sveriges fyra elområden SE1–SE4, med dygnets medel-, lägsta och högsta pris i öre/kWh exklusive moms. Priserna kommer från Nord Pool och publiceras för morgondagen omkring klockan 13. Datan är fri att använda, även kommersiellt, med länk till Elmarknad.se som källa.",
    url: `${SITE_URL}${pagePath}`,
    sameAs: [`${SITE_URL}/elpriser`, `${SITE_URL}/spotpris`],
    keywords: ["spotpris", "elpris", "Nord Pool", "elområde", "SE1", "SE2", "SE3", "SE4"],
    inLanguage: "sv-SE",
    isAccessibleForFree: true,
    license: `${SITE_URL}/elpris-api`,
    creator: { "@id": ORGANIZATION_ID, "@type": "Organization", name: "Elmarknad.se", url: `${SITE_URL}/` },
    publisher: { "@id": ORGANIZATION_ID },
    spatialCoverage: { "@type": "Place", name: "Sverige (elområde SE1, SE2, SE3 och SE4)" },
    temporalCoverage: `${SPOT_PRICES_FIRST_DATE}/..`,
    variableMeasured: [
      { "@type": "PropertyValue", name: "Spotpris", unitText: "öre/kWh exkl. moms" },
      { "@type": "PropertyValue", name: "Spotpris", unitText: "SEK/kWh exkl. moms" },
    ],
    distribution: [
      ...AREAS.map((area) => ({
        "@type": "DataDownload",
        name: `Dagens spotpriser ${area} (JSON)`,
        encodingFormat: "application/json",
        contentUrl: `${ELPRIS_API_BASE}/v1/prices/today_${area}.json`,
      })),
      {
        "@type": "DataDownload",
        name: "OpenAPI-beskrivning av elpris-API:t",
        encodingFormat: "application/vnd.oai.openapi+json",
        contentUrl: OPENAPI_URL,
      },
    ],
  };
}
